import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Eye, Building, Users, Sparkles, Heart, Stethoscope, Monitor, TreePine, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const IMG_MEDITATION = "https://images.unsplash.com/photo-1763723341417-a4ea5ba6f675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIwc3Bpcml0dWFsJTIwd2VsbG5lc3MlMjBwZWFjZWZ1bHxlbnwwfHx8fDE3NzYwNzczMjd8MA&ixlib=rb-4.1.0&q=85";
const IMG_COMMUNITY = "https://images.unsplash.com/photo-1524069290683-0457abfe42c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzZXJ2aWNlJTIwdm9sdW50ZWVyaW5nJTIwSW5kaWF8ZW58MHx8fHwxNzc2MDc3MzI3fDA&ixlib=rb-4.1.0&q=85";
const IMG_HEALTHCARE = "https://static.prod-images.emergentagent.com/jobs/928e083d-76f5-471c-8405-fe8d0803371a/images/ffab65a0e108be7611e1fac02d06405cc485f0f9bb52117ea7e290636b0e2172.png";
const IMG_DIGITAL = "https://images.unsplash.com/photo-1771765764892-91f2ed4ddbf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwZWR1Y2F0aW9uJTIwdGVjaG5vbG9neSUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3NzYwNzczMjd8MA&ixlib=rb-4.1.0&q=85";
const IMG_ENVIRONMENT = "https://static.prod-images.emergentagent.com/jobs/928e083d-76f5-471c-8405-fe8d0803371a/images/02e7b62035f3925b6e35e4318c18c52079cad8f7be0eba72e55b45a1b0296bf2.png";

const banners = [
  { img: IMG_MEDITATION, label: "Meditation & Spiritual Wellness" },
  { img: IMG_COMMUNITY, label: "Community Service & Social Welfare" },
  { img: IMG_HEALTHCARE, label: "Healthcare Access & Preventive Care" },
  { img: IMG_DIGITAL, label: "Digital & Technical Empowerment" },
];

const trustBadges = [
  { icon: ShieldCheck, text: "Registered Section 8 Organization" },
  { icon: Eye, text: "Transparent & Accountable Governance" },
  { icon: Building, text: "CSR-Ready Structure" },
  { icon: Users, text: "Community-Centric Approach" },
];

const focusAreas = [
  { icon: Sparkles, title: "Spiritual & Mental Wellness", color: "#E28743",
    objective: "Promote mental well-being and inner awareness through meditation.",
    why: "Rising stress and emotional imbalance demand inner stability for lasting progress.",
    approach: "Meditation camps, Vipassana introductions, stress management workshops." },
  { icon: Heart, title: "Social Welfare & Inclusion", color: "#606C38",
    objective: "Ensure access to government schemes and social security for vulnerable groups.",
    why: "Millions remain deprived of benefits due to lack of awareness and documentation.",
    approach: "Awareness drives on yojanas, pension facilitation, community help centres." },
  { icon: Stethoscope, title: "Healthcare Access", color: "#8ECAE6",
    objective: "Improve basic healthcare access and promote preventive wellness.",
    why: "Healthcare remains inaccessible for many in rural and semi-urban India.",
    approach: "Free health camps, preventive care drives, doctor collaborations." },
  { icon: Monitor, title: "Digital & Technical Empowerment", color: "#E28743",
    objective: "Bridge the digital divide with essential technology skills.",
    why: "Digital literacy is critical for education, employment, and services access.",
    approach: "Computer training, digital awareness workshops, online safety programs." },
  { icon: TreePine, title: "Holistic Community Development", color: "#606C38",
    objective: "Build sustainable, balanced, and self-reliant communities.",
    why: "Long-term development requires harmony between people, environment, and lifestyle.",
    approach: "Tree plantation, cleanliness drives, sustainable living education." },
];

const programs = [
  { title: "Meditation for All (M4A)", desc: "Awareness programs for stress-free and mindful living.", tag: "Wellness", audience: "All age groups", impact: "10,000+ lives through meditation camps" },
  { title: "Sahyog Seva Kendras", desc: "Assistance for pensions and government schemes.", tag: "Social", audience: "Elderly, widows, underserved", impact: "50+ community centres planned" },
  { title: "Swasthya Sahayata Abhiyan", desc: "Grassroots preventive healthcare initiatives.", tag: "Healthcare", audience: "Rural & semi-urban populations", impact: "100+ health camps annually" },
  { title: "Tech Chetna", desc: "Digital literacy and awareness programs.", tag: "Technology", audience: "Youth, women, rural communities", impact: "5,000+ individuals trained" },
  { title: "Green Mangalam", desc: "Environmental sustainability and community initiatives.", tag: "Environment", audience: "Schools, colleges, communities", impact: "50,000+ trees planted" },
];

const whyDifferent = [
  { point: "Integrated spiritual + social approach", detail: "Addressing inner well-being and external development together" },
  { point: "Grassroots execution strategy", detail: "Working directly at community level for maximum reach" },
  { point: "Scalable and CSR-aligned structure", detail: "Section 8 framework ready for corporate partnerships" },
  { point: "Focus on long-term transformation", detail: "Building sustainable systems, not short-term relief" },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div data-testid="home-page">
      {/* SECTION 1 — HERO with Dynamic Banners */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        {banners.map((banner, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === currentBanner ? "opacity-100" : "opacity-0"}`}
          >
            <img src={banner.img} alt={banner.label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1B201E]/80 via-[#1B201E]/50 to-transparent" />
          </div>
        ))}
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-28 pb-20 w-full">
          <div className="max-w-2xl">
            <p className="text-[#E28743] text-sm font-bold tracking-[0.2em] uppercase mb-5 font-['Outfit'] animate-fade-in-up">From Awareness to Action</p>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.15] mb-5 animate-fade-in-up delay-100">
              BSM Welfare Foundation &ndash; Building a Spiritually Aware and Socially Empowered Society
            </h1>
            <p className="text-white/75 text-base sm:text-lg leading-relaxed max-w-xl mb-8 animate-fade-in-up delay-200">
              Integrating meditation, social welfare, healthcare, and digital empowerment to create sustainable and meaningful impact across communities.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <Link to="/programs" data-testid="hero-explore-btn" className="bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-7 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press inline-flex items-center gap-2 text-sm">
                Explore Our Work <ArrowRight size={16} />
              </Link>
              <Link to="/get-involved" data-testid="hero-involve-btn" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-7 py-3.5 font-medium transition-all duration-300 btn-press text-sm">
                Get Involved
              </Link>
            </div>
          </div>
          {/* Banner nav */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4">
            <button onClick={() => setCurrentBanner((p) => (p - 1 + banners.length) % banners.length)} data-testid="banner-prev" className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"><ChevronLeft size={18} /></button>
            <div className="flex gap-2">
              {banners.map((_, i) => (
                <button key={i} onClick={() => setCurrentBanner(i)} data-testid={`banner-dot-${i}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentBanner ? "bg-[#E28743] w-8" : "bg-white/40 w-2"}`} />
              ))}
            </div>
            <button onClick={() => setCurrentBanner((p) => (p + 1) % banners.length)} data-testid="banner-next" className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — TRUST & CREDIBILITY STRIP */}
      <section data-testid="trust-strip" className="bg-white border-b border-[#E2E5E3]/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {trustBadges.map((badge, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-[#2C3531]" data-testid={`trust-badge-${i}`}>
                <badge.icon size={18} className="text-[#606C38]" strokeWidth={1.5} />
                <span className="font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — ABOUT SNAPSHOT */}
      <section data-testid="about-snapshot" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">
              A Holistic Approach to Social Transformation
            </h2>
            <p className="text-[#7A827E] text-base sm:text-lg leading-relaxed">
              BSM Welfare Foundation (Bhavatu Sabba Mangalam) is a registered non-profit organization dedicated to creating a balance between inner well-being and outer progress. Rooted in the philosophy of compassion, awareness, and right action, the foundation works to address both mental and societal challenges through a unified and sustainable model.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 3B — PHILOSOPHY: Inner Balance. Outer Impact. */}
      <section data-testid="philosophy-section" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Our Philosophy</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-5">
                Inner Balance. <span className="gradient-text-saffron">Outer Impact.</span>
              </h2>
              <p className="text-[#7A827E] text-base leading-relaxed mb-5">
                At BSM Welfare Foundation, we believe that true social transformation begins with inner awareness. When individuals cultivate mindfulness, emotional resilience, and spiritual clarity, they naturally extend compassion, service, and responsibility to those around them.
              </p>
              <p className="text-[#7A827E] text-base leading-relaxed mb-5">
                Our approach uniquely connects ancient meditation wisdom with modern grassroots social work, healthcare outreach, and digital empowerment. This integration creates a sustainable cycle: awareness leads to compassion, compassion leads to action, and action leads to lasting community transformation.
              </p>
              <div className="bg-[#F3F0E6] rounded-2xl p-5 border border-[#E2E5E3]/50">
                <p className="text-[#E28743] font-['Outfit'] font-semibold text-sm">Strengthen the mind &rarr; Empower the individual &rarr; Transform the community</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E28743]/5 rounded-3xl rotate-2" />
                <img src={IMG_MEDITATION} alt="Inner balance through meditation" className="relative rounded-2xl w-full h-[380px] object-cover shadow-[0_20px_40px_rgba(0,0,0,0.08)]" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 4 — OUR CORE FOCUS AREAS */}
      <section data-testid="focus-areas-section" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-14">
            <p className="overline mb-3">What We Focus On</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E]">Our Focus Areas</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {focusAreas.map((area, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <Card data-testid={`focus-card-${i}`} className="bg-white border-0 rounded-3xl p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 group h-full">
                  <CardContent className="p-0">
                    <div className="w-13 h-13 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${area.color}15`, width: 52, height: 52 }}>
                      <area.icon size={24} style={{ color: area.color }} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-3">{area.title}</h3>
                    <div className="space-y-2.5">
                      <div>
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#E28743] mb-0.5">Objective</p>
                        <p className="text-[#7A827E] text-xs leading-relaxed">{area.objective}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#606C38] mb-0.5">Why It Matters</p>
                        <p className="text-[#7A827E] text-xs leading-relaxed">{area.why}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#023047] mb-0.5">Approach</p>
                        <p className="text-[#7A827E] text-xs leading-relaxed">{area.approach}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — OUR PROGRAMS */}
      <section data-testid="programs-section" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-14">
            <p className="overline mb-3">What We Do</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E]">Our Key Initiatives</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((prog, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <Card data-testid={`program-card-${i}`} className="bg-white border-0 rounded-3xl p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 group h-full">
                  <CardContent className="p-0">
                    <Badge className="bg-[#606C38]/10 text-[#606C38] border-0 rounded-full px-3 py-1 text-xs font-medium mb-4 hover:bg-[#606C38]/20">{prog.tag}</Badge>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-2">{prog.title}</h3>
                    <p className="text-[#7A827E] text-sm leading-relaxed mb-3">{prog.desc}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
                      <span className="text-[10px] text-[#E28743] font-semibold uppercase tracking-wider">Target: <span className="text-[#7A827E] normal-case font-normal">{prog.audience}</span></span>
                      <span className="text-[10px] text-[#606C38] font-semibold uppercase tracking-wider">Impact: <span className="text-[#7A827E] normal-case font-normal">{prog.impact}</span></span>
                    </div>
                    <Link to="/programs" className="inline-flex items-center gap-1.5 text-sm text-[#E28743] font-medium hover:gap-2.5 transition-all duration-300">
                      Learn More <ArrowRight size={14} />
                    </Link>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — COMMITMENT TO TRANSPARENCY */}
      <section data-testid="transparency-section" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Our Promise</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">
                Built on Trust, Transparency & Accountability
              </h2>
              <p className="text-[#7A827E] text-base leading-relaxed mb-5">
                As a Section 8 registered organization under the Companies Act 2013, BSM Welfare Foundation operates with a professional governance framework. Every initiative is backed by transparent processes, ethical decision-making, and a commitment to long-term sustainability.
              </p>
              <p className="text-[#7A827E] text-base leading-relaxed">
                We maintain strict accountability standards — from financial audits and regulatory compliance to stakeholder reporting and public fund-utilization disclosures. Our governance structure ensures that every rupee contributed creates measurable, verifiable impact.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Section 8 Company", sub: "Companies Act, 2013" },
                  { label: "PAN Registered", sub: "Tax compliance verified" },
                  { label: "12A Registration", sub: "Applied / In process" },
                  { label: "80G Certification", sub: "Applied / In process" },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)] text-center" data-testid={`compliance-badge-${i}`}>
                    <ShieldCheck size={20} className="text-[#606C38] mx-auto mb-2" strokeWidth={1.5} />
                    <p className="font-['Outfit'] font-semibold text-sm text-[#1B201E]">{item.label}</p>
                    <p className="text-[10px] text-[#7A827E] mt-0.5">{item.sub}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY BSM FOUNDATION */}
      <section data-testid="why-bsm-section" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Our Edge</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-5">
                What Makes Us Different
              </h2>
              <p className="text-[#7A827E] text-base leading-relaxed mb-8">
                While many organizations focus only on external challenges, BSM Welfare Foundation addresses both inner well-being and external development — creating a more sustainable and human-centred model of impact.
              </p>
              <ul className="space-y-5">
                {whyDifferent.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`why-item-${i}`}>
                    <CheckCircle size={18} className="text-[#606C38] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <span className="text-[#2C3531] font-semibold block">{item.point}</span>
                      <span className="text-[#7A827E] text-sm">{item.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#8ECAE6]/10 rounded-3xl -rotate-2" />
                <img src={IMG_ENVIRONMENT} alt="BSM Foundation making a difference" className="relative rounded-2xl w-full h-[380px] object-cover shadow-[0_20px_40px_rgba(0,0,0,0.08)]" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 8 — CALL TO ACTION */}
      <section data-testid="cta-section" className="py-20 sm:py-28 bg-[#1B201E] relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern-dots opacity-5" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-5">
              Be a Part of Meaningful Change
            </h2>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-2xl mx-auto mb-10">
              Your support can help create a society that is aware, empowered, and self-reliant.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-involved" data-testid="cta-volunteer" className="bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press text-sm">Become a Volunteer</Link>
              <Link to="/get-involved" data-testid="cta-partner" className="bg-[#606C38] text-white hover:bg-[#4B552A] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(96,108,56,0.3)] btn-press text-sm">Partner With Us</Link>
              <Link to="/get-involved" data-testid="cta-support" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-3.5 font-medium transition-all duration-300 btn-press text-sm">Support Our Initiatives</Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 9 — SEO FOOTER TEXT */}
      <section data-testid="seo-footer" className="py-12 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-[#7A827E] text-sm leading-relaxed text-center max-w-4xl mx-auto mb-3">
            BSM Welfare Foundation is a registered Section 8 non-profit organization (NGO) in India focused on holistic development through meditation, social welfare, healthcare access, and digital literacy. We work at the intersection of spiritual wellness and social impact to create sustainable change.
          </p>
          <p className="text-[#7A827E] text-xs leading-relaxed text-center max-w-4xl mx-auto">
            As a CSR-ready NGO, we collaborate with individuals, communities, corporates, and government bodies to drive meaningful social impact across India. Our programs span meditation awareness, pension and scheme facilitation, preventive healthcare camps, digital empowerment, and environmental sustainability — all built on a foundation of transparency, ethical governance, and community-first execution.
          </p>
        </div>
      </section>
    </div>
  );
}
